import cookies from 'vue-cookies'
import { store } from '@/store'
import SetupDashboard from '@/components/dashboard/SetupDashboard'
import Dashboard from '@/components/dashboard/Dashboard'
import DynamicModuleRegistrationUtil from '@utils/dynamicModuleRegistration'
import BaseRoutesEnum from '@/enums/modules/routes/base'
import OnboardingRoutesEnum from '@/enums/modules/routes/onboarding'
import NotificationsRoutesEnum from '@/enums/modules/routes/notifications'
import { OnboardingService } from '../services/onboarding'
const AllNotificationsComponent = () => import(/* webpackChunkName: "Dashboard" */ '@/components/dashboard/notifications/AllNotifications')

const SetupDashboardModule = () => import(/* webpackChunkName: "SetupDashboard" */'@/store/modules/setupDashboard')
const DashboardModule = () => import(/* webpackChunkName: "Dashboard" */'@/store/modules/dashboard')
const QualifierStoreModule = () => import(/* webpackChunkName: "SetupGuide" */ '@/store/modules/user/qualifier')
const AnnouncementsModule = () => import(/* webpackChunkName: "Announcements" */ '@/store/modules/dashboard/announcements')
const OrderStatusModule = () => import(/* webpackChunkName: "OrderStatus" */ '@/store/modules/dashboard/orderStatus')
const DashboardCarouselModule = () => import(/* webpackChunkName: "DashboardCarousel" */ '@/store/modules/dashboard/carousel')
const DashboardFulfillmentMetricsModule = () => import(/* webpackChunkName: "DashboardFulfillmentMetricsModule" */ '@/store/modules/dashboard/fulfillmentMetrics')
const DashboardInventoryReorderModule = () => import(/* webpackChunkName: "DashboardInventoryReorder" */ '@/store/modules/dashboard/inventoryReorder')
const DashboardCustomOnboardingChecklistModule = () => import(/* webpackChunkName: "DashboardCustomOnboardingChecklistModule" */ '@/store/modules/dashboard/customOnboardingChecklist')
const DashboardNotificationsModule = () => import(/* webpackChunkName: "DashboardNotificationsModule" */ '@/store/modules/dashboard/notifications')
const PricingStoreModule = () => import(/* webpackChunkName: "Pricing" */ '../store/modules/pricing')

const dynamicModuleRegistrationArgsSetupDashboard = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'setupDashboard',
    module: SetupDashboardModule,
    store,
    modulePath: 'setupDashboard'
  }
}

const dynamicModuleRegistrationArgsDashboard = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'dashboard',
    module: DashboardModule,
    store,
    modulePath: 'dashboard'
  }
}

const dynamicModuleRegistrationArgsAnnouncements = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'announcements',
    module: AnnouncementsModule,
    store,
    modulePath: ['dashboard', 'announcements']
  }
}

const dynamicModuleRegistrationArgsOrderStatus = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'orderStatus',
    module: OrderStatusModule,
    store,
    modulePath: ['dashboard', 'orderStatus']
  }
}

const dynamicModuleRegistrationArgsDashboardCarousel = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'carousel',
    module: DashboardCarouselModule,
    store,
    modulePath: ['dashboard', 'carousel']
  }
}

const dynamicModuleRegistrationArgsDashboardFulfillmentMetrics = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'fulfillmentMetrics',
    module: DashboardFulfillmentMetricsModule,
    store,
    modulePath: ['dashboard', 'fulfillmentMetrics']
  }
}

const dynamicModuleRegistrationArgsDashboardInventoryReorder = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'inventoryReorder',
    module: DashboardInventoryReorderModule,
    store,
    modulePath: ['dashboard', 'inventoryReorder']
  }
}

const dynamicModuleRegistrationArgsDashboardCustomOnboardingChecklist = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'customOnboardingChecklist',
    module: DashboardCustomOnboardingChecklistModule,
    store,
    modulePath: ['dashboard', 'customOnboardingChecklist']
  }
}

const dynamicModuleRegistrationArgsDashboardNotifications = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'notifications',
    module: DashboardNotificationsModule,
    store,
    modulePath: ['dashboard', 'notifications']
  }
}

const dynamicModuleRegistrationPricingArgs = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'pricing',
    module: PricingStoreModule,
    store,
    modulePath: 'pricing'
  }
}

const dynamicModuleRegistrationArgsQualifier = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'qualifier',
    module: QualifierStoreModule,
    store,
    modulePath: ['user', 'qualifier']
  }
}

//  `source` either 'dashboard' or 'setup'
const determineShouldDashboardRedirect = (source = 'dashboard') => {
  const { isOnboarding, impersonatedRoleId } = store.getters['user/getUser']
  const isAdmin = impersonatedRoleId === 9
  if (source === 'dashboard') return isOnboarding && !isAdmin
  if (source === 'setup') return !isOnboarding || isAdmin
}

const checkOnboardingShown = async () => {
  return store.getters['onboarding/onboardingShown']
}

export const shouldUserOnboard = async (query) => {
  const { impersonatedRoleId } = store.getters['user/getUser']
  const isAdmin = impersonatedRoleId === 9
  const forceOnboarding = query && query.utm_source === 'testing'
  const onboardingWizardCompleted = store.getters['onboarding/completed']
  const onboardingShown = await checkOnboardingShown()

  return !isAdmin && !onboardingWizardCompleted && (onboardingShown || forceOnboarding)
}

export default [
  {
    path: '/dashboard',
    name: BaseRoutesEnum.DashboardV2,
    component: Dashboard,
    meta: {showSideNav: true},
    async beforeEnter (to, from, next) {
      await DynamicModuleRegistrationUtil(dynamicModuleRegistrationArgsDashboard())
      await DynamicModuleRegistrationUtil(dynamicModuleRegistrationArgsAnnouncements())
      await DynamicModuleRegistrationUtil(dynamicModuleRegistrationArgsOrderStatus())
      await DynamicModuleRegistrationUtil(dynamicModuleRegistrationArgsDashboardCarousel())
      await DynamicModuleRegistrationUtil(dynamicModuleRegistrationArgsDashboardFulfillmentMetrics())
      await DynamicModuleRegistrationUtil(dynamicModuleRegistrationArgsDashboardInventoryReorder())
      await DynamicModuleRegistrationUtil(dynamicModuleRegistrationArgsDashboardNotifications())
      const integrationType = from.query.type
      if (await shouldUserOnboard(from.query)) {
        if (integrationType) {
          return next({ name: OnboardingRoutesEnum.Onboarding, query: { type: integrationType } })
        } else {
          return next({ name: OnboardingRoutesEnum.Onboarding })
        }
      }

      const shouldRedirect = determineShouldDashboardRedirect()
      if (shouldRedirect) {
        if (integrationType) {
          return next({ name: BaseRoutesEnum.SetupDashboard, query: { type: integrationType } })
        } else {
          return next({ name: BaseRoutesEnum.SetupDashboard })
        }
      }
      return next()
    }
  },
  {
    path: '/notifications',
    name: NotificationsRoutesEnum.AllNotifications,
    component: AllNotificationsComponent,
    meta: { showSideNav: true },
    async beforeEnter (to, from, next) {
      await DynamicModuleRegistrationUtil(dynamicModuleRegistrationArgsDashboard())
      await DynamicModuleRegistrationUtil(dynamicModuleRegistrationArgsDashboardNotifications())
      next()
    }
  },
  {
    path: '/setup',
    name: BaseRoutesEnum.SetupDashboard,
    component: SetupDashboard,
    meta: {showSideNav: true},
    async beforeEnter (to, from, next) {
      await DynamicModuleRegistrationUtil(dynamicModuleRegistrationArgsDashboard())
      await DynamicModuleRegistrationUtil(dynamicModuleRegistrationArgsSetupDashboard())
      await DynamicModuleRegistrationUtil(dynamicModuleRegistrationArgsQualifier())
      await DynamicModuleRegistrationUtil(dynamicModuleRegistrationArgsDashboardCustomOnboardingChecklist())
      await DynamicModuleRegistrationUtil(dynamicModuleRegistrationPricingArgs())
      const integrationType = from.query.type
      if (await shouldUserOnboard(from.query)) {
        if (integrationType) {
          return next({ name: OnboardingRoutesEnum.Onboarding, query: { type: integrationType } })
        } else {
          return next({ name: OnboardingRoutesEnum.Onboarding })
        }
      }

      const shouldRedirect = determineShouldDashboardRedirect('setup')
      if (shouldRedirect) {
        if (integrationType) {
          return next({ name: BaseRoutesEnum.DashboardV2, query: { type: integrationType } })
        } else {
          return next({ name: BaseRoutesEnum.DashboardV2 })
        }
      }
      return next()
    }
  }
]
